import type { PanelCommand } from "./panel-commands";
import { QUERY_KEYS } from "./query-keys";

/**
 * Maps a finished agent tool call to the panel commands that keep the web
 * content panel in sync (e.g. the CV preview refreshes after the agent
 * rewrites the summary). PURE by design: no React, no query client; the
 * subscriber in the web shell owns the side effects.
 *
 * Tool names below are the agent's registered tool keys
 * (apps/api/src/agent/mastra-agent.service.ts, `tools: {...}`): camelCase
 * keys like `cvUpdateSummary`, NOT dot-notation ids.
 */

export interface PanelSyncToolActivity {
  toolName: string;
  /** Server status: 'completed' | 'failed' | 'declined' | ... */
  status?: string;
  isError?: boolean;
  args?: Record<string, unknown>;
  result?: unknown;
}

// Query-key prefixes. Where a canonical constant exists in QUERY_KEYS it is
// used; the cv/profile keys are defined privately next to their hooks, so the
// stable prefixes are mirrored here with pointers to the source of truth.

/** `useTailoredCvApi.ts`: CVS_KEY = ['cvs'], cvKey/resolvedKey/overridesKey = ['cv', id, ...]. */
const CV_LIST_KEY = ["cvs"] as const;
const CV_DETAIL_ROOT = ["cv"] as const;
/** `useProfileApi.ts`: profileKey = ['profile', target ?? 'self']; the master CV renders from this. */
const PROFILE_ROOT = ["profile"] as const;
/** `usePublicProfileBuilder` etc.: ['public-profile', ...]. */
const PUBLIC_PROFILE_ROOT = ["public-profile"] as const;
/** `useJobsApi.ts`: apply history list (My jobs → Applied). */
const JOB_CATALOG_HISTORY_ROOT = ["job-catalog", "history"] as const;
/** `useJobsApi.ts`: per-job detail (shows the applied state on the job page). */
const JOB_CATALOG_DETAIL_ROOT = ["job-catalog", "detail"] as const;
/** Legacy native-job applications (`QUERY_KEYS.JOB_APPLICATIONS`). */
const JOB_APPLICATIONS_ROOT = ["jobs", "applications"] as const;
/** `query-keys.ts`: company-wide Applications inbox (list + talent detail). */
const COMPANY_APPLICATIONS_ROOT = ["company-applications"] as const;
/** `QUERY_KEYS.COMPANY_JOBS` / `EMPLOYER_APPLICANTS` share this prefix. */
const COMPANY_JOBS_ROOT = ["jobs", "company"] as const;
/** All catalog feeds, saved jobs, and per-job user state. */
const JOB_CATALOG_ROOT = ["job-catalog"] as const;
/** `query-keys.ts`: every Application Studio project and artifact cache. */
const APPLICATION_STUDIO_ROOT = ["application-studio"] as const;
/** Notification badge and delivery-preference queries. The feed itself has a refresh command. */
const NOTIFICATIONS_ROOT = ["notifications"] as const;
/** Messaging badges and query-backed preference data. The conversation list also has a refresh command. */
const MESSAGING_ROOT = ["messaging"] as const;

const CV_KEYS: readonly (readonly string[])[] = [
  CV_LIST_KEY,
  CV_DETAIL_ROOT,
  // The master CV preview is rendered from profile data, so CV writes must
  // refresh it too (cvUpdateSummary on the main CV lands in the profile).
  PROFILE_ROOT,
];

const PROFILE_KEYS: readonly (readonly string[])[] = [
  PROFILE_ROOT,
  // Tailored CVs resolve on top of the profile, so profile edits change what
  // every CV preview shows.
  CV_LIST_KEY,
  CV_DETAIL_ROOT,
];

const APPLICATION_KEYS: readonly (readonly string[])[] = [
  JOB_CATALOG_HISTORY_ROOT,
  JOB_CATALOG_DETAIL_ROOT,
  JOB_APPLICATIONS_ROOT,
];

const PUBLISH_KEYS: readonly (readonly string[])[] = [
  ...CV_KEYS,
  PUBLIC_PROFILE_ROOT,
];

/**
 * Tool name → query-key prefixes invalidated when the tool COMPLETES.
 * Read-only tools are deliberately absent: they change nothing.
 */
const INVALIDATION_BY_TOOL: Record<string, readonly (readonly string[])[]> = {
  // CV writes
  cvCreate: CV_KEYS,
  cvUpdate: CV_KEYS,
  cvUpdateSummary: CV_KEYS,
  cvUpdateOverrides: CV_KEYS,
  cvSetFitToOnePage: CV_KEYS,
  cvTailor: CV_KEYS,
  cvDuplicate: CV_KEYS,
  cvDelete: CV_KEYS,
  // skillRun writes a sibling CV (guided CV builder / skill application).
  skillRun: CV_KEYS,
  // Publishing also changes the public SmartCV surface.
  cvPublish: PUBLISH_KEYS,
  cvUnpublish: PUBLISH_KEYS,
  cvShareLinkCreate: CV_KEYS,
  cvShareLinkUpdate: CV_KEYS,
  cvShareLinkRevoke: CV_KEYS,
  cvAccessGrantCreate: CV_KEYS,
  cvAccessGrantUpdate: CV_KEYS,
  cvAccessGrantRevoke: CV_KEYS,
  cvSharedProfileUpdate: CV_KEYS,
  cvSharedSectionUpsert: CV_KEYS,
  cvSharedSectionDelete: CV_KEYS,
  cvSharedStructuredUpdate: CV_KEYS,
  cvHandoffDraft: CV_KEYS,
  cvHandoffSend: CV_KEYS,
  cvHandoffCancel: CV_KEYS,

  // Profile writes (the CV preview renders from these)
  profileUpdate: PROFILE_KEYS,
  careerHighlightUpsert: PROFILE_KEYS,
  careerHighlightDelete: PROFILE_KEYS,
  profileSkillGroupsUpdate: PROFILE_KEYS,
  profileLanguagesUpdate: PROFILE_KEYS,
  profileJobPreferencesUpdate: PROFILE_KEYS,
  profilePrivateInfoUpdate: PROFILE_KEYS,
  profileCvConfigUpdate: PROFILE_KEYS,
  profileSectionsConfigUpdate: PROFILE_KEYS,
  profilePrivacyUpdate: PUBLISH_KEYS,
  publicProfileBlocksUpdate: [PUBLIC_PROFILE_ROOT],
  publicProfileSocialsUpdate: [PUBLIC_PROFILE_ROOT],
  publicProfileContactsUpdate: [PUBLIC_PROFILE_ROOT],
  publicProfileDesignUpdate: [PUBLIC_PROFILE_ROOT],
  publicProfileSettingsUpdate: [PUBLIC_PROFILE_ROOT],
  experienceUpsert: PROFILE_KEYS,
  educationUpsert: PROFILE_KEYS,
  certificationUpsert: PROFILE_KEYS,
  projectUpsert: PROFILE_KEYS,
  serviceOfferUpsert: PROFILE_KEYS,
  experienceDelete: PROFILE_KEYS,
  educationDelete: PROFILE_KEYS,
  certificationDelete: PROFILE_KEYS,
  projectDelete: PROFILE_KEYS,
  serviceOfferDelete: PROFILE_KEYS,

  // Application submits / status changes (My jobs + job detail applied state)
  applicationSubmit: APPLICATION_KEYS,
  catalogApplySubmit: APPLICATION_KEYS,
  applicationWithdraw: APPLICATION_KEYS,
  // Employer status moves also change the company Applications inbox and the
  // per-job applicant list/detail caches (both live under jobs/company).
  applicationUpdateStatus: [
    ...APPLICATION_KEYS,
    COMPANY_APPLICATIONS_ROOT,
    COMPANY_JOBS_ROOT,
  ],
  // Employer job lifecycle changes reshape the company job list (and the
  // Careers block / applications destination filter derived from it).
  jobPublish: [COMPANY_JOBS_ROOT],
  jobClose: [COMPANY_JOBS_ROOT],

  // Personal inbox writes. The local-state conversation list also receives a
  // messages.refresh command below.
  conversationCreate: [MESSAGING_ROOT],
  conversationSend: [MESSAGING_ROOT],
  conversationMarkRead: [MESSAGING_ROOT],
  conversationDelete: [MESSAGING_ROOT],
  messagingPreferencesUpdate: [MESSAGING_ROOT],
  userBlock: [MESSAGING_ROOT],
  userUnblock: [MESSAGING_ROOT],
  conversationMute: [MESSAGING_ROOT],
  conversationUnmute: [MESSAGING_ROOT],

  // Notification writes. The local-state feed also receives a
  // notifications.refresh command below.
  notificationMarkRead: [NOTIFICATIONS_ROOT],
  notificationMarkAllRead: [NOTIFICATIONS_ROOT],
  notificationPreferencesUpdate: [NOTIFICATIONS_ROOT],
  notificationDelete: [NOTIFICATIONS_ROOT],

  // Persisted Application Studio projects and artifacts.
  studioProjectCreate: [APPLICATION_STUDIO_ROOT],
  studioProjectUpdate: [APPLICATION_STUDIO_ROOT],
  studioArtifactGenerate: [APPLICATION_STUDIO_ROOT],
  studioArtifactUpdate: [APPLICATION_STUDIO_ROOT],
  studioArtifactRegenerate: [APPLICATION_STUDIO_ROOT],
  studioArtifactDelete: [APPLICATION_STUDIO_ROOT],
  studioApplySubmit: [APPLICATION_STUDIO_ROOT, ...APPLICATION_KEYS],
  catalogJobSave: [JOB_CATALOG_ROOT],
  catalogJobUnsave: [JOB_CATALOG_ROOT],
  catalogJobHide: [JOB_CATALOG_ROOT],
  catalogJobUnhide: [JOB_CATALOG_ROOT],
};

const MESSAGE_REFRESH_TOOLS = new Set([
  "conversationCreate",
  "conversationSend",
  "conversationMarkRead",
  "conversationDelete",
  "messagingPreferencesUpdate",
  "userBlock",
  "userUnblock",
  "conversationMute",
  "conversationUnmute",
]);

const NOTIFICATION_REFRESH_TOOLS = new Set([
  "notificationMarkRead",
  "notificationMarkAllRead",
  "notificationDelete",
]);

const NOVA_WRITE_TOOLS = new Set([
  "novaCompile",
  "novaActivate",
  "novaPause",
  "novaResume",
  "novaRunNow",
]);

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function firstString(value: unknown): string | null {
  return stringArray(value)[0] ?? null;
}

function deriveJobFilterCommand(
  activity: PanelSyncToolActivity,
): PanelCommand | null {
  if (activity.toolName !== "catalogJobSearch") return null;
  const args = activity.args ?? {};
  return {
    type: "jobs.filters.apply",
    filters: {
      query: typeof args.query === "string" ? args.query : "",
      location: stringArray(args.locations),
      workMode: firstString(args.workModes),
      jobType: firstString(args.employmentTypes),
      source: firstString(args.sources),
      applyMode: firstString(args.applyModes),
      datePosted: typeof args.datePosted === "string" ? args.datePosted : null,
    },
  };
}

/**
 * Agent edits make a new version of one file: its detail (the head), its
 * versions, its head checks and its site all change, so every card and the
 * open canvas panel read them again (user test fix, 2026-09-25). Suggest mode
 * changes nothing.
 */
const ARTIFACT_EDIT_TOOLS = new Set(["updatePage", "updateDocument", "editImage"]);

function field(value: unknown, key: string): unknown {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)[key]
    : undefined;
}

export function artifactEditInvalidationKeys(
  activity: PanelSyncToolActivity,
): (readonly string[])[] {
  if (!ARTIFACT_EDIT_TOOLS.has(activity.toolName)) return [];
  if (field(activity.args, "mode") === "suggest" || field(activity.result, "mode") === "suggest") {
    return [];
  }
  const id = field(activity.result, "artifactId") ?? field(activity.args, "artifactId");
  if (typeof id !== "string" || !id) return [];
  return [
    QUERY_KEYS.ARTIFACT(id),
    QUERY_KEYS.ARTIFACT_VERSIONS(id),
    QUERY_KEYS.ARTIFACT_HEAD_CHECKS(id),
    QUERY_KEYS.ARTIFACT_SITE(id),
  ];
}

function isSuccessfulResult(activity: PanelSyncToolActivity): boolean {
  if (activity.isError) return false;
  const status = activity.status?.toLowerCase();
  if (status === "failed" || status === "declined" || status === "rejected") {
    return false;
  }
  return true;
}

function didNovaToolMutate(activity: PanelSyncToolActivity) {
  if (activity.toolName !== "novaCompile") return true;
  if (!activity.result || typeof activity.result !== "object") return true;
  const outcome = (activity.result as Record<string, unknown>).outcome;
  return outcome === undefined || outcome === "created_draft";
}

/**
 * Derives the panel commands for one tool result. Most read-only tools return
 * `[]`; catalog search is the intentional exception because its arguments are
 * also visible jobs-panel filters. Unknown and failed/declined runs are no-ops.
 */
export function derivePanelCommands(
  activity: PanelSyncToolActivity,
): PanelCommand[] {
  if (!activity.toolName || !isSuccessfulResult(activity)) {
    return [];
  }

  const jobFilterCommand = deriveJobFilterCommand(activity);
  if (jobFilterCommand) return [jobFilterCommand];
  if (NOVA_WRITE_TOOLS.has(activity.toolName) && didNovaToolMutate(activity)) {
    return [{ type: "nova.refresh" }];
  }

  const keys = [
    ...(INVALIDATION_BY_TOOL[activity.toolName] ?? []),
    ...artifactEditInvalidationKeys(activity),
  ];

  // De-duplicate prefixes (CV + publish sets overlap).
  const seen = new Set<string>();
  const uniqueKeys = keys.filter((key) => {
    const serialized = JSON.stringify(key);
    if (seen.has(serialized)) return false;
    seen.add(serialized);
    return true;
  });

  const commands: PanelCommand[] = [];
  if (uniqueKeys.length > 0) {
    commands.push({ type: "data.invalidate", keys: uniqueKeys });
  }
  if (MESSAGE_REFRESH_TOOLS.has(activity.toolName)) {
    commands.push({ type: "messages.refresh" });
  }
  if (NOTIFICATION_REFRESH_TOOLS.has(activity.toolName)) {
    commands.push({ type: "notifications.refresh" });
  }
  return commands;
}
